import React from 'react';
import { cn } from '@/lib/utils';
import type { Worker } from '../../engine/data';
import { calcShiftCDI, calcShiftPLI } from '../../engine/scores';
import { ShiftCdiBar, ShiftPliBar } from '../../ScoreWidgets';
import { Panel, Pill, dataTable, toneText } from '../../ui';
import type { Tone } from '../../ui';

type Shift = Worker['shifts'][number];

function duration(s: Shift): string {
  if (s.in && s.out) {
    const [ih, im] = s.in.split(':').map(Number);
    const [oh, om] = s.out.split(':').map(Number);
    let mins = oh * 60 + om - (ih * 60 + im);
    if (mins < 0) mins += 1440;
    return `${(mins / 60).toFixed(1)}h`;
  }
  if (s.in && s.out === null) return 'On shift';
  return '—';
}

const Dash: React.FC = () => <span className='text-[11px] text-muted-foreground'>—</span>;

export const RecentShifts: React.FC<{ w: Worker }> = ({ w }) => (
  <Panel
    title='Recent Shifts'
    sub='CDI Impact — circadian cost vs habitual pattern  ·  PLI — estimated physical load per shift'
  >
    <div className={dataTable.wrap}>
      <table className={dataTable.table}>
        <thead>
          <tr>
            {['Date', 'Type', 'Start', 'End', 'Duration'].map((h) => (
              <th key={h} className={dataTable.th}>
                {h}
              </th>
            ))}
            <th className={cn(dataTable.th, dataTable.num)}>Drive km</th>
            <th className={dataTable.th}>CDI Impact</th>
            <th className={dataTable.th}>Phys Load</th>
            <th className={cn(dataTable.th, dataTable.num)}>kcal est.</th>
            <th className={dataTable.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {w.shifts.map((s, i) => {
            const cdi = calcShiftCDI(s, w);
            const pli = calcShiftPLI(s, w);
            const isOff = !s.in;
            const cdiTone: Tone = cdi ? (cdi.score >= 60 ? 'bad' : cdi.score >= 35 ? 'warn' : 'good') : 'muted';
            return (
              <tr key={`${s.date}-${i}`} className={cn(dataTable.tr, isOff && 'opacity-45')}>
                <td className={dataTable.td}>{s.date}</td>
                <td className={dataTable.td}>
                  {cdi ? (
                    <span className={cn('text-[11px] font-semibold', toneText[cdiTone])}>{cdi.type}</span>
                  ) : (
                    <span className='text-[11px] text-muted-foreground'>Rest</span>
                  )}
                </td>
                <td className={dataTable.td}>{s.in || '—'}</td>
                <td className={dataTable.td}>
                  {s.out === null && s.in ? <Pill tone='info'>Active</Pill> : s.out || '—'}
                </td>
                <td className={dataTable.td}>{duration(s)}</td>
                <td className={cn(dataTable.td, dataTable.num)}>{s.drive || 0}</td>
                <td className={dataTable.td}>{cdi ? <ShiftCdiBar score={cdi.score} /> : <Dash />}</td>
                <td className={dataTable.td}>{pli ? <ShiftPliBar score={pli.score} /> : <Dash />}</td>
                <td className={cn(dataTable.td, dataTable.num, 'text-[11px] text-muted-foreground')}>
                  {pli ? pli.kcal.toLocaleString() : '—'}
                </td>
                <td className={dataTable.td}>
                  {isOff ? (
                    <Pill tone='muted'>Rest</Pill>
                  ) : s.out === null ? (
                    <Pill tone='info'>Live</Pill>
                  ) : (
                    <Pill tone='muted'>Complete</Pill>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Panel>
);
